<script lang="ts">
export default {
  name: 'KkProductBulkPreviewModal',
}
</script>
<script setup lang="ts" >
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { computed } from 'vue'
import { KkModal, KkButton, KkBox, KkLoading } from '../../../../components/base'
const store = useStore()
const { t } = useI18n()

interface OptionButton {
  url?: string
}
interface GmbSyncResult {
  coordinationCancellationStoreCount: number
  errorCount: number
  status: string
}
interface GmbSyncStatus {
  id: number
  kind: string
}
interface ProductStore {
  id: number
  optionButtonUrl: string
  storeName: string
  uuid: string
  gmbSyncStatus: GmbSyncStatus
}
interface Product {
  categoryName: string
  description: string
  editable: boolean
  gmbSyncResult: GmbSyncResult
  id: string
  image: string
  optionButton?: OptionButton
  price?: string
  productCategoryId: number
  productStores: ProductStore[]
  registeredStoreCount: number
  title: string
}
interface Props {
  isVisible: boolean
  products: Product[]
}
const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
  products: () => [],
})
const emit = defineEmits(['submit', 'close'])
const bulkSetProductsForPreview = computed<Product[]>(() => store.getters['companiesProducts/bulkSetProductsForPreview'])
const productsLoading = computed<boolean>(() => store.getters['companiesProducts/loading'])
</script>

<template>
  <KkModal :is-visible="props.isVisible" width="720px" @change="emit('close')">
    <KkLoading v-if="productsLoading" />
    <div v-if="!productsLoading">
      <div class="p-modal__content__header">
        {{ t('pages.CompaniesProductNew.form.preview.title') }}
      </div>
      <div class="p-modal__content__body">
        <div class="productList">
          <KkBox v-for="product in products" :key="product.id" class="productInfo" tag="div" shadow>
            <template #content>
              <div>
                <img :src="product.image" :alt="product.title" class="productInfo__image">
                <div class="productInfo__name">{{ product.title }}</div>
              </div>
            </template>
          </KkBox>
        </div>
        <div v-if="products.length" class="productTable">
          <div class="productTable__title">{{ t('pages.CompaniesProductNew.form.preview.buttonUrl') }}</div>
          <div class="p-table__content">
            <table>
              <thead>
                <tr>
                  <th class="productTable__productName">{{ t('pages.CompaniesProductNew.form.preview.productName') }}</th>
                  <th class="productTable__storeName">{{ t('pages.CompaniesProductNew.form.preview.storeName') }}</th>
                  <th class="productTable__url">{{ t('pages.CompaniesProductNew.form.preview.url') }}</th>
                </tr>
                <tr class="p-table__content__space">
                  <td colspan="4" />
                </tr>
              </thead>
              <tbody>
                <template v-for="product in bulkSetProductsForPreview" :key="product.id">
                  <tr v-for="(productStore, index) in product.productStores" :key="index">
                    <td v-if="index == 0" :rowSpan="product.productStores.length">{{ product.title }}</td>
                    <td>{{ productStore.storeName ? productStore.storeName : '-' }}</td>
                    <td>{{ productStore.optionButtonUrl ? productStore.optionButtonUrl : '-' }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
            <div class="tableBottomBorder" />
          </div>
        </div>
      </div>
      <div class="p-modal__content__footer">
        <div class="c-button c-button--right">
          <KkButton
            class="footerCancelBtn"
            color="secondary"
            outlined
            :text="t('labels.close')"
            @click="emit('close')"
          />
          <KkButton
            color="primary"
            outlined
            :text="t('labels.confirm')"
            @click="emit('submit')"
          />
        </div>
      </div>
    </div>
  </KkModal>
</template>
<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';

.productList {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}
.productInfo {
  margin: 8px;

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    margin-bottom: 8px;
    object-fit: cover;
    border: 1px solid #E3E3E3;
    border-radius: 4px;
  }

  &__name {
    font-size: 14px;
    font-weight: bold;
  }
}
.productTable {
  &__title {
    font-size: 12px;
    font-weight: bold;
    margin: 24px 0;
  }
  height: 100%;
  &__productName {
    width: 30%;
  }
  &__storeName {
    width: 30%;
  }
  &__url {
    width: 40%;
  }
  td:empty {
    display: table-cell;
  }
  tr:last-child {
    td {
      border-bottom: none;
    }
  }
}

.footerCancelBtn {
  margin-right: 8px;
}
.tableBottomBorder {
  border-top: 1px solid #E4E7EB;
}
</style>
